import { Button, Modal, cx } from '@ims/ui'
import {
  Flashlight,
  FlashlightOff,
  Pause,
  Plus,
  ScanBarcode,
  Search,
  SwitchCamera,
} from '@ims/ui/icons'
import {
  useEffect,
  useEffectEvent,
  useId,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from 'react'
import { createApi, suggestedName } from './lib/api.ts'
import {
  acceptScan,
  addScannedSerial,
  cleanCode,
  GUIDE_BOX,
  pinchZoom,
  zoomSteps,
  type LastScan,
} from './lib/scan.ts'
import type { ScanInfo, ScanSession } from './platform.ts'
import { useSession } from './session.tsx'
import { useWorkspace } from './workspace.tsx'

// Scanning with the device's camera. The camera and the decoding are the
// platform's (`platform.scanner`); this is the screen around them. A scanned
// code is handed to the caller exactly as a typed one would be.

/** What a scanned code led to: shown under the camera view. */
export interface ScanResult {
  ok: boolean
  message: string
  /** Something to do about it ("Add product"): the scanner closes, then it runs. */
  action?: { label: string; run: () => void }
  /**
   * The code, when no product has it. The scanner stops reading and says so on a card
   * over the picture, which stays until it is answered: at a counter a line of words
   * under the camera went unseen, and the item went unbilled.
   */
  unknown?: string
}

type Mode = 'single' | 'continuous'

/**
 * What a counter has scanned so far, shown under the camera: `list` below the status line,
 * `summary` beside Done. With it the scanner fills a phone's screen.
 */
export interface ScanCart {
  list: ReactNode
  summary?: ReactNode
}

/** A phone-sized window, checked when the scanner opens. */
function phoneWidth(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
}

/** Why the camera could not start, in words a shopkeeper can act on. */
function cameraProblem(error: unknown): string {
  const name = error instanceof Error ? error.name : ''
  if (name === 'NotAllowedError' || name === 'SecurityError')
    return 'The camera is not allowed for this app. Allow it in your browser’s site settings, then try again.'
  if (name === 'NotFoundError' || name === 'OverconstrainedError')
    return 'No camera was found on this device.'
  if (name === 'NotReadableError') return 'Another app is using the camera. Close it and try again.'
  return 'The camera could not be started.'
}

/**
 * What the open product databases call a code no product has, for its card. Online
 * only, and never waited for: the card is up before the answer.
 */
function useSuggestedName(code: string | null): { looking: boolean; name: string | null } {
  const { platform } = useSession()
  const tenantId = useWorkspace().current?.tenant_id
  const [found, setFound] = useState<{ code: string; name: string | null } | null>(null)
  useEffect(() => {
    if (!code || !tenantId) return
    let closed = false
    void createApi(platform.apiUrl, platform.supabase)
      .lookupBarcode(tenantId, code)
      .then(
        ({ suggestion }) => (suggestion ? suggestedName(suggestion) : null),
        // Offline, not a code the databases take, or no answer: the card goes without a name.
        () => null,
      )
      .then((name) => {
        if (!closed) setFound({ code, name })
      })
    return () => {
      closed = true
    }
  }, [code, tenantId, platform])
  if (!code || !tenantId) return { looking: false, name: null }
  return found?.code === code ? { looking: false, name: found.name } : { looking: true, name: null }
}

/**
 * The camera with a guide box. `single` closes after the first code; `continuous`
 * stays open and keeps reading (a counter scanning item after item). A code no product
 * has keeps either open, on its card.
 */
export function ScannerDialog({
  mode,
  title = 'Scan a barcode',
  onCode,
  onClose,
  cart,
}: {
  mode: Mode
  title?: string
  onCode: (code: string) => Promise<ScanResult> | ScanResult
  onClose: () => void
  cart?: ScanCart
}) {
  const { platform } = useSession()
  const [full] = useState(() => !!cart && phoneWidth())
  const video = useRef<HTMLVideoElement>(null)
  const session = useRef<ScanSession | null>(null)
  const last = useRef<LastScan | null>(null)
  const busy = useRef(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [canTorch, setCanTorch] = useState(false)
  const [torch, setTorch] = useState(false)
  const [result, setResult] = useState<ScanResult | null>(null)
  // What this camera can do (each control shows only where the device offers it).
  const [steps, setSteps] = useState<number[]>([])
  const [zoom, setZoom] = useState<number | null>(null)
  const [canSwitch, setCanSwitch] = useState(false)
  const [focus, setFocus] = useState<{ x: number; y: number } | null>(null)
  const [info, setInfo] = useState<ScanInfo | null>(null)
  // Nothing read for a few seconds: nearly always the pack is too close to focus on.
  const [slow, setSlow] = useState(false)
  const waiting = ready && !result
  // A code no product has: its card is up and nothing more is read until it is answered.
  const held = useRef(false)
  const unknown = result?.unknown ?? null
  const [closing, setClosing] = useState(false)
  const suggestion = useSuggestedName(result?.action ? unknown : null)
  const missId = useId()
  const card = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (unknown) card.current?.focus()
  }, [unknown])
  const scanAgain = () => {
    // The card fades out first (150ms, the wrapper's transition), then reading goes on.
    setClosing(true)
    setTimeout(() => {
      held.current = false
      setResult(null)
      setClosing(false)
    }, 150)
  }
  useEffect(() => {
    if (!waiting) return
    const timer = setTimeout(() => setSlow(true), 4000)
    return () => clearTimeout(timer)
  }, [waiting])

  /** Reads the controls off the running camera (again after switching to another lens). */
  const readControls = (started: ScanSession) => {
    setCanTorch(!!started.torch)
    setSteps(started.zoom ? zoomSteps(started.zoom.min, started.zoom.max) : [])
    setZoom(started.zoom ? started.zoom.value() : null)
    setCanSwitch(!!started.nextCamera)
  }

  const handle = useEffectEvent(async (raw: string) => {
    const code = cleanCode(raw)
    if (!code) return
    const now = Date.now()
    // One scan per pack held in view: a repeat only refreshes when it was last seen.
    if (!acceptScan(last.current, code, now)) {
      last.current = { code, atMs: now }
      return
    }
    // One lookup at a time; a code that arrives meanwhile is read again in a moment.
    if (busy.current || held.current) return
    busy.current = true
    last.current = { code, atMs: now }
    try {
      const outcome = await onCode(code)
      if (outcome.ok) platform.scanner.confirm()
      else if (outcome.unknown) {
        held.current = true
        platform.scanner.miss()
      }
      setResult(outcome)
      if (mode === 'single' && !outcome.unknown) onClose()
    } finally {
      busy.current = false
    }
  })

  // One camera session at a time on this video: a start waits for the one before it to
  // finish starting and be stopped (React runs effects twice in development).
  const settled = useRef<Promise<void>>(Promise.resolve())
  useEffect(() => {
    let closed = false
    const element = video.current
    if (!element) return
    settled.current = settled.current.then(async () => {
      if (closed) return
      try {
        const started = await platform.scanner.start(element, (code) => void handle(code), {
          region: GUIDE_BOX,
        })
        if (closed) return started.stop()
        session.current = started
        setReady(true)
        readControls(started)
      } catch (error) {
        if (!closed) setProblem(cameraProblem(error))
      }
    })
    return () => {
      closed = true
      session.current?.stop()
      session.current = null
    }
  }, [platform])

  const zoomTo = (level: number) => {
    const control = session.current?.zoom
    if (!control) return
    void control
      .set(level)
      .then(() => setZoom(control.value()))
      .catch(() => {})
  }
  const switchCamera = () => {
    const current = session.current
    void current
      ?.nextCamera?.()
      .then(() => {
        setTorch(false)
        readControls(current)
      })
      .catch(() => {})
  }

  // Two fingers zoom; one finger that does not move focuses where it touched.
  const fingers = useRef(new Map<number, { x: number; y: number }>())
  const pinch = useRef<{ distance: number; zoom: number } | null>(null)
  const moved = useRef(false)
  const spread = () => {
    const [a, b] = [...fingers.current.values()]
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0
  }
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    fingers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    moved.current = fingers.current.size > 1
    const control = session.current?.zoom
    pinch.current =
      fingers.current.size === 2 && control ? { distance: spread(), zoom: control.value() } : null
  }
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const was = fingers.current.get(e.pointerId)
    if (!was) return
    if (Math.hypot(e.clientX - was.x, e.clientY - was.y) > 8) moved.current = true
    const control = session.current?.zoom
    if (!pinch.current || !control) return
    fingers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    const next = pinchZoom(
      pinch.current.zoom,
      pinch.current.distance,
      spread(),
      control.min,
      control.max,
    )
    // The camera is asked only when the change would show.
    if (Math.abs(next - control.value()) >= 0.1) zoomTo(next)
  }
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const tapped = fingers.current.size === 1 && !moved.current
    fingers.current.delete(e.pointerId)
    pinch.current = null
    const focusAt = session.current?.focusAt
    if (!tapped || !focusAt || (e.target as HTMLElement).closest('button')) return
    const box = e.currentTarget.getBoundingClientRect()
    const at = { x: (e.clientX - box.left) / box.width, y: (e.clientY - box.top) / box.height }
    setFocus(at)
    void focusAt(at.x, at.y).catch(() => {})
    setTimeout(() => setFocus((f) => (f === at ? null : f)), 900)
  }

  // A long press on the words under the picture shows what the scanner is working with:
  // a screenshot of it says why a phone reads badly.
  const press = useRef<ReturnType<typeof setTimeout>>(undefined)
  const showInfo = info !== null
  useEffect(() => {
    if (!showInfo) return
    const tick = setInterval(() => setInfo(session.current?.info() ?? null), 1000)
    return () => clearInterval(tick)
  }, [showInfo])
  const startPress = () => {
    press.current = setTimeout(
      () => setInfo((shown) => (shown ? null : (session.current?.info() ?? null))),
      600,
    )
  }
  const endPress = () => clearTimeout(press.current)

  const toggleTorch = () => {
    const next = !torch
    void session.current
      ?.torch?.(next)
      .then(() => setTorch(next))
      .catch(() => setCanTorch(false))
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size={full ? 'full' : cart ? 'md' : 'sm'}
      footer={
        <>
          {cart?.summary && <div className="mr-auto min-w-0">{cart.summary}</div>}
          <Button variant={mode === 'continuous' ? 'primary' : 'secondary'} onClick={onClose}>
            {mode === 'continuous' ? 'Done' : 'Cancel'}
          </Button>
        </>
      }
    >
      {problem ? (
        <div className="space-y-2 py-2 text-sm">
          <p className="font-medium">{problem}</p>
          <p className="text-muted">You can still type the code or search by name.</p>
        </div>
      ) : (
        <div className={cx('space-y-3', full && 'flex h-full flex-col')}>
          <div
            className="relative aspect-[4/3] shrink-0 touch-none overflow-hidden rounded-xl bg-black select-none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <video ref={video} muted playsInline className="size-full object-cover" />
            {/* What the reader looks at (GUIDE_BOX): a square in the middle. */}
            <div
              aria-hidden
              style={{
                left: `${GUIDE_BOX.x * 100}%`,
                top: `${GUIDE_BOX.y * 100}%`,
                width: `${GUIDE_BOX.w * 100}%`,
                height: `${GUIDE_BOX.h * 100}%`,
              }}
              className={cx(
                'pointer-events-none absolute rounded-2xl border-2 border-dashed shadow-[0_0_0_100vmax_rgb(0_0_0/0.35)]',
                result?.ok ? 'border-success' : 'border-white/90',
              )}
            />
            {focus && (
              <span
                aria-hidden
                style={{ left: `${focus.x * 100}%`, top: `${focus.y * 100}%` }}
                className="pointer-events-none absolute size-12 -translate-x-1/2 -translate-y-1/2 animate-scale-in rounded-full border-2 border-white"
              />
            )}
            {steps.length > 0 && (
              <div
                role="group"
                aria-label="Zoom"
                className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1 rounded-full bg-black/55 p-1"
              >
                {steps.map((level) => (
                  <button
                    key={level}
                    type="button"
                    aria-pressed={zoom !== null && Math.abs(zoom - level) < 0.25}
                    onClick={() => zoomTo(level)}
                    className={cx(
                      'size-9 rounded-full text-xs font-semibold',
                      zoom !== null && Math.abs(zoom - level) < 0.25
                        ? 'bg-white text-black'
                        : 'text-white',
                    )}
                  >
                    {level}×
                  </button>
                ))}
              </div>
            )}
            {canSwitch && (
              <button
                type="button"
                aria-label="Switch camera"
                title="Switch camera"
                onClick={switchCamera}
                className="absolute bottom-3 left-3 flex size-12 items-center justify-center rounded-full bg-black/55 text-white"
              >
                <SwitchCamera className="size-5" />
              </button>
            )}
            {canTorch && (
              <button
                type="button"
                aria-label={torch ? 'Turn the light off' : 'Turn the light on'}
                aria-pressed={torch}
                onClick={toggleTorch}
                className="absolute bottom-3 right-3 flex size-12 items-center justify-center rounded-full bg-black/55 text-white"
              >
                {torch ? <FlashlightOff className="size-5" /> : <Flashlight className="size-5" />}
              </button>
            )}
            {unknown && (
              <div
                className={cx(
                  'absolute inset-0 transition-opacity duration-150 ease-out',
                  closing && 'opacity-0',
                )}
                onPointerDown={(e) => e.stopPropagation()}
                onPointerUp={(e) => e.stopPropagation()}
              >
                <div aria-hidden className="absolute inset-0 animate-dim-in bg-black/50" />
                <div className="absolute inset-0 grid place-items-center p-3.5">
                  <div
                    ref={card}
                    role="alertdialog"
                    aria-labelledby={missId}
                    tabIndex={-1}
                    className="w-full animate-pop-in rounded-card bg-raised p-3.5 shadow-modal outline-none"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-warning-light text-warning">
                        <ScanBarcode className="size-5" aria-hidden />
                      </span>
                      <div className="min-w-0">
                        <p id={missId} className="font-semibold">
                          Not in your products
                        </p>
                        <p className="truncate font-mono text-xs text-muted">{unknown}</p>
                      </div>
                    </div>
                    {suggestion.looking ? (
                      <p className="mt-2.5 flex items-center gap-2 rounded-lg bg-soft px-2.5 py-2 text-sm text-muted">
                        <Search className="size-4 shrink-0 text-primary" aria-hidden />
                        Looking for its name…
                      </p>
                    ) : suggestion.name ? (
                      <p className="mt-2.5 rounded-lg bg-soft px-2.5 py-2">
                        <span className="block text-xs font-medium text-muted">Looks like</span>
                        <span className="block text-sm font-semibold">{suggestion.name}</span>
                      </p>
                    ) : (
                      !result?.action && (
                        <p className="mt-2.5 text-sm text-muted">
                          Ask a manager to add it, then scan it again.
                        </p>
                      )
                    )}
                    <div className="mt-3 flex gap-2">
                      <Button
                        className="flex-1 whitespace-nowrap"
                        variant={result?.action ? 'secondary' : 'primary'}
                        onClick={scanAgain}
                      >
                        Scan again
                      </Button>
                      {result?.action && (
                        <Button
                          className="flex-1 whitespace-nowrap"
                          icon={<Plus className="size-4" />}
                          onClick={() => {
                            const { run } = result.action!
                            onClose()
                            run()
                          }}
                        >
                          {result.action.label}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex min-h-10 shrink-0 flex-wrap items-center justify-between gap-2">
            <p
              role="status"
              onPointerDown={startPress}
              onPointerUp={endPress}
              onPointerLeave={endPress}
              onContextMenu={(e) => e.preventDefault()}
              className={cx(
                'text-sm select-none',
                result && !unknown
                  ? result.ok
                    ? 'font-medium text-success'
                    : 'text-danger'
                  : 'text-muted',
              )}
            >
              {unknown && <Pause className="mr-1.5 inline size-4 align-[-3px]" aria-hidden />}
              {unknown
                ? 'Scanning is paused'
                : result
                  ? result.message
                  : ready
                    ? slow
                      ? `Nothing read yet. Move back a little and hold steady${canSwitch ? ', or switch camera' : ''}.`
                      : 'Hold the barcode in the box, 15 to 20 cm away.'
                    : 'Starting the camera…'}
            </p>
            {result?.action && !unknown && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  const { run } = result.action!
                  onClose()
                  run()
                }}
              >
                {result.action.label}
              </Button>
            )}
          </div>
          {info && (
            <p data-testid="scan-info" className="font-mono text-[11px] leading-snug text-muted">
              {info.reader} · {info.width}×{info.height}
              {info.zoom !== null && ` · ${info.zoom.toFixed(1)}×`}
              {info.camera && ` · ${info.camera}`}
              {info.autofocus ? ' · autofocus' : ' · fixed focus'}
              {info.light && ' · light'}
            </p>
          )}
          {cart && (
            <div
              className={cx(
                '-mx-5 border-t border-border',
                full && 'min-h-0 flex-1 overflow-y-auto',
              )}
            >
              {cart.list}
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

/**
 * A button that opens the camera to scan. Renders nothing where the browser has
 * no camera API. The camera is asked for only when the button is tapped.
 */
export function ScanButton({
  mode,
  onCode,
  title,
  label = 'Scan with the camera',
  className,
  cart,
}: {
  mode: Mode
  onCode: (code: string) => Promise<ScanResult> | ScanResult
  title?: string
  /** What has been scanned so far, listed under the camera (a counter's bill). */
  cart?: ScanCart
  /** What the button is called: say what it scans when a screen has more than one. */
  label?: string
  className?: string
}) {
  const { platform } = useSession()
  const [open, setOpen] = useState(false)
  if (!platform.scanner.available()) return null
  return (
    <>
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={() => setOpen(true)}
        className={cx(
          'flex size-9 items-center justify-center rounded-lg text-primary hover:bg-primary-light',
          className,
        )}
      >
        <ScanBarcode className="size-5" aria-hidden />
      </button>
      {open && (
        <ScannerDialog
          mode={mode}
          title={title}
          onCode={onCode}
          onClose={() => setOpen(false)}
          cart={cart}
        />
      )}
    </>
  )
}

/**
 * Scans serial numbers into a box that lists one per line (receiving stock, a new
 * product's opening serials). Each unit is scanned once; the camera stays open.
 */
export function ScanSerialsButton({
  text,
  onText,
  label,
  className,
}: {
  text: string
  onText: (text: string) => void
  label: string
  className?: string
}) {
  return (
    <ScanButton
      mode="continuous"
      title="Scan serial numbers"
      label={label}
      className={className}
      onCode={(code) => {
        const added = addScannedSerial(text, code)
        if (!added) return { ok: false, message: `${code} is already in the list` }
        onText(added.text)
        return { ok: true, message: `${added.count} scanned · ${code}` }
      }}
    />
  )
}
